import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

// Declared before the class: customElements.define upgrades existing cards (and runs connectedCallback) at module load.
const CLAID_FRAME_PROBE_TIMEOUT_MS = 2000;

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);
    this.addEventListener('error', this.#handleClaidExtraImageError, true);
    this.#lockClaidListingSlides();
    this.#stripClaidExtraShopifyFallback();
    this.#syncClaidExtraSlides();
    this.#applyClaidOnlyFrames();
    this.#logClaidListingImage();

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #logClaidListingImage() {
    const image = this.querySelector('img.product-media__image, img[data-shopify-src]');
    if (!(image instanceof HTMLImageElement)) return;

    const src = image.currentSrc || image.src || '';
    const shopifySrc = image.getAttribute('data-shopify-src') || '';

    if (typeof window.__claidLog === 'function') {
      window.__claidLog('PLP', 'card image', {
        productId: this.dataset.productId || '',
        asksE01: /_E01/.test(src),
        cloudinaryDefault01: /\/d_[^/]+_01\.(?:jpe?g|png|webp)/i.test(src),
        hasShopifySrc: Boolean(shopifySrc),
        src,
        shopifySrc,
      });
    }
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#syncClaidExtraSlides();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
      this.#syncClaidExtraSlides();
    }
  }

  /**
   * Claid cards may only show the enhanced hero plus `_02`–`_04` extras.
   * Leftover Shopify generic slides stay in the DOM for swatches but must
   * never be reachable by hover or arrows.
   */
  #lockClaidListingSlides() {
    const { slideshow } = this.refs;
    if (!slideshow?.hasAttribute('data-claid-hero-slide-id')) return;

    const heroSlideId = slideshow.getAttribute('data-claid-hero-slide-id');
    const slides = slideshow.refs?.slides || [];

    for (const slide of slides) {
      const isHero = slide.getAttribute('slide-id') === heroSlideId;
      const isExtra = slide.hasAttribute('data-claid-extra-slide');
      if (isHero || isExtra) continue;
      if (slide.hasAttribute('variant-image')) continue;

      slide.setAttribute('hidden', '');
      slide.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Some stores only want a subset of the Claid frames (e.g. `03,04` for stores 470 / 395,
   * whose `_01` / `_02` frames carry the wrong logo). Liquid sets `data-claid-only-frames`
   * on the slideshow; the first kept frame becomes the card image, the rest stay as hover
   * extras. Applied only when all 4 frames exist, otherwise the card is left untouched.
   */
  async #applyClaidOnlyFrames() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const onlyFrames = (slideshow.getAttribute('data-claid-only-frames') || '')
      .split(',')
      .map((frame) => frame.trim())
      .filter(Boolean);
    if (!onlyFrames.length) return;

    try {
      await this.#restrictClaidFrames(slideshow, onlyFrames);
    } catch (error) {
      // Fail open: keep the regular card gallery.
      if (typeof window.__claidLog === 'function') {
        window.__claidLog('PLP', 'frame subset failed', error instanceof Error ? error.message : String(error));
      }
    } finally {
      slideshow.setAttribute('data-claid-frames-resolved', '');
    }
  }

  /**
   * @param {Element} slideshow
   * @param {string[]} onlyFrames
   */
  async #restrictClaidFrames(slideshow, onlyFrames) {
    const heroSlideId = slideshow.getAttribute('data-claid-hero-slide-id');
    const heroSlide = Array.from(slideshow.querySelectorAll('slideshow-slide')).find(
      (slide) => slide.getAttribute('slide-id') === heroSlideId
    );
    const heroImage = heroSlide?.querySelector('img.product-media__image');
    if (!(heroImage instanceof HTMLImageElement)) return;

    const heroSrc = heroImage.getAttribute('src') || '';
    const heroSuffix = /_(E?)01$/i;
    if (!heroSuffix.test(heroSrc)) return;

    // Probe small renditions of all four frames; the hero URL is the template for the others.
    const frameUrl = (url, frame) => url.replace(/_(E?)01(?=[\s,]|$)/gi, `_$1${frame}`);
    const probeUrl = (frame) => frameUrl(heroSrc, frame).replace(/\/c_limit,w_\d+/, '/c_limit,w_120');
    const frames = ['01', '02', '03', '04'];
    const exists = await withTimeout(
      Promise.all(frames.map((frame) => claidImageExists(probeUrl(frame)))),
      CLAID_FRAME_PROBE_TIMEOUT_MS
    ).catch(() => []);
    if (exists.length !== frames.length || !exists.every(Boolean)) return;

    const [heroFrame, ...extraFrames] = onlyFrames;

    // The first kept frame replaces the hero image.
    heroImage.src = frameUrl(heroSrc, heroFrame);
    if (heroImage.srcset) heroImage.srcset = frameUrl(heroImage.srcset, heroFrame);
    const maxResolution = heroImage.getAttribute('data_max_resolution');
    if (maxResolution) heroImage.setAttribute('data_max_resolution', frameUrl(maxResolution, heroFrame));

    // Every other extra slide is hidden for good; `#syncClaidExtraSlides` skips `data-claid-missing`.
    slideshow.querySelectorAll('slideshow-slide[data-claid-extra-slide]').forEach((slide) => {
      if (extraFrames.includes(slide.getAttribute('data-claid-frame') || '')) return;

      slide.setAttribute('hidden', '');
      slide.setAttribute('aria-hidden', 'true');
      slide.setAttribute('data-claid-missing', '');
    });

    this.#syncClaidExtraSlides();

    // Keep the card hidden until the swapped hero is ready so it never reveals blank.
    await withTimeout(
      new Promise((resolve) => {
        if (heroImage.complete && heroImage.naturalWidth > 0) return resolve(true);
        heroImage.addEventListener('load', () => resolve(true), { once: true });
        heroImage.addEventListener('error', () => resolve(false), { once: true });
      }),
      CLAID_FRAME_PROBE_TIMEOUT_MS
    ).catch(() => {});
  }

  /**
   * Extra frames must never restore `data-shopify-src`. If Liquid still emitted
   * a Shopify CDN URL, hide that slide instead of showing the backup.
   */
  #stripClaidExtraShopifyFallback() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    slideshow.querySelectorAll('slideshow-slide[data-claid-extra-slide] img').forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;

      image.removeAttribute('data-shopify-src');
      image.addEventListener('load', this.#handleClaidExtraImageLoad, { once: true });
      if (image.complete && image.naturalWidth > 0) this.#handleClaidExtraImageLoad({ target: image });
    });
  }

  /**
   * @param {Event} event
   */
  #handleClaidExtraImageLoad = (event) => {
    const image = event.target;
    if (!(image instanceof HTMLImageElement)) return;

    const src = image.currentSrc || image.src || '';
    if (!src || (image.complete && image.naturalWidth === 0) || /cdn\.shopify\.com/i.test(src)) {
      this.#hideClaidExtraSlide(image);
      return;
    }

    const slide = image.closest('slideshow-slide[data-claid-extra-slide]');
    const frame = slide?.getAttribute('data-claid-frame');
    const publicId = src.split('/').pop()?.split('?')[0] || '';
    if (frame && publicId && !new RegExp(`_E?${frame}(?:\\.[a-z0-9]+)?$`, 'i').test(publicId)) {
      this.#hideClaidExtraSlide(image);
    }
  };

  /**
   * @param {HTMLImageElement} image
   */
  #hideClaidExtraSlide(image) {
    const slide = image.closest('slideshow-slide[data-claid-extra-slide]');
    if (!slide) return;

    slide.setAttribute('hidden', '');
    slide.setAttribute('aria-hidden', 'true');
    slide.setAttribute('data-claid-missing', '');
    image.removeAttribute('srcset');
    image.removeAttribute('src');
    this.#syncClaidExtraSlides();
  }

  /**
   * Extra `_02`–`_04` slides belong to the hero color. Hide them when another
   * variant image is selected so sliding cannot mix colors or originals.
   */
  #syncClaidExtraSlides() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const extras = slideshow.querySelectorAll('slideshow-slide[data-claid-extra-slide]');
    if (!extras.length) return;

    const heroSlideId = slideshow.getAttribute('data-claid-hero-slide-id');
    const selectedImageId = this.variantPicker?.selectedOption?.dataset.optionMediaId;
    const onHero = !selectedImageId || !heroSlideId || selectedImageId === heroSlideId;

    extras.forEach((slide) => {
      if (slide.hasAttribute('data-claid-missing')) return;
      slide.hidden = !onHero;
    });

    const visibleExtras = slideshow.querySelectorAll('slideshow-slide[data-claid-extra-slide]:not([hidden])');
    slideshow.setAttribute('data-generic-media-size', String(onHero ? 1 + visibleExtras.length : 1));
    slideshow.updatedCallback?.();
  }

  /**
   * @param {Event} event
   */
  #handleClaidExtraImageError = (event) => {
    const image = event.target;
    if (!(image instanceof HTMLImageElement)) return;

    const slide = image.closest('slideshow-slide[data-claid-extra-slide]');
    if (!slide) return;

    this.#hideClaidExtraSlide(image);
  };

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    const extra = slideshow.querySelector('slideshow-slide[data-claid-extra-slide]:not([hidden])');
    if (extra) {
      const extraId = extra.getAttribute('slide-id');
      if (extraId) slideshow.select({ id: extraId }, undefined, { animate: false });
      return;
    }

    // Claid cards must not fall through to leftover Shopify original slides.
    if (slideshow.hasAttribute('data-claid-hero-slide-id')) return;

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}

/**
 * @param {string} url
 * @returns {Promise<boolean>}
 */
function claidImageExists(url) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image.naturalWidth > 2);
    image.onerror = () => resolve(false);
    image.src = url;
  });
}

/**
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @returns {Promise<T>}
 */
function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}
